import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import useHomepageExperiencePlace from './useHomepageExperiencePlace'
import ExperienceOfferCarousel from './ExperienceOfferCarousel'
import Clickable from 'components/Common/Clickable'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import ModalContext from 'contexts/ModalContext'
import { ReportError } from 'actions/ErrorActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import IconButton from 'LuxKit/Button/IconButton'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

interface HomepageExperienceOfferCarouselProps {
  place: App.Place
  minimumOffersCount?: number
  streamingEnabled?: boolean
}

function HomepageExperienceOfferCarousel(props: HomepageExperienceOfferCarouselProps) {
  const { place, minimumOffersCount, streamingEnabled } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()

  const openLocationModal = useCallback(async() => {
    try {
      const { default: ExperiencesPageLocationModal } = await import(
        'components/Experiences/ExperiencesPage/ExperiencesPageLocationModal/ExperiencesPageLocationModal'
      )
      showModal(<ExperiencesPageLocationModal />)
    } catch(error) {
      dispatch(ReportError(error, { modal: 'ExperiencesPageLocationModal' }))
    }
  }, [dispatch, showModal])

  const experienceHeading = useMemo(() => {
    const placeName = formatPlacePhrase({
      location: { name: place.name },
      language: intl.locale,
      intent: 'location',
    })

    return intl.formatMessage(
      {
        id: '6lmm1-O4mkcc6GL00FWD',
        defaultMessage: 'Find experiences around <location>{placeName}</location>',
        description: 'Heading for experience offers carousel with clickable location',
      },
      {
        placeName,
        location: (chunks: React.ReactNode) => (
          <Clickable key="location" onClick={openLocationModal}>
            <HeadingTextBlock
              endIcon={
                <IconButton kind="tertiary" variant="default" nonInteractable>
                  <LineAngleDownIcon />
                </IconButton>
              }
              variant="heading2alt"
            >
              {chunks}
            </HeadingTextBlock>
          </Clickable>
        ),
      },
    )
  }, [intl, place.name, openLocationModal])

  return (
    <ExperienceOfferCarousel
      place={place}
      experienceHeading={experienceHeading}
      minimumOffersCount={minimumOffersCount}
      streamingEnabled={streamingEnabled}
    />
  )
}

interface Props {
  minimumOffersCount?: number
  streamingEnabled?: boolean
}

function SmartExperienceOfferCarousel(props: Props) {
  const { minimumOffersCount, streamingEnabled } = props
  const place = useHomepageExperiencePlace()

  if (!place) {
    return null
  }

  return <HomepageExperienceOfferCarousel place={place} minimumOffersCount={minimumOffersCount} streamingEnabled={streamingEnabled} />
}

export default SmartExperienceOfferCarousel
